/**
 * Sent times for RFIs (#608).
 *
 * "Send with Outlook" opens a mailto: draft, and the app cannot see whether the
 * person pressed Send in Outlook. So a send is recorded when they CONFIRM it,
 * with the time they give, and it can be corrected afterwards.
 *
 * ⚠️ Every time here is EASTERN wall-clock, whatever the browser's zone. The
 * API reads the `YYYY-MM-DDTHH:mm` it is given as Eastern (that is the office's
 * clock, and the one the people reading the record use), so the form has to
 * fill and display Eastern too — otherwise a PM on a laptop set to Central
 * would see a default an hour off and save it. The labels say "ET" so nobody
 * has to guess.
 */
import { EASTERN_TZ } from '@/modules/manpower/lib/gridDates';

// Built once — Intl formatters are comparatively expensive to construct.
const PARTS = new Intl.DateTimeFormat('en-US', {
  timeZone: EASTERN_TZ,
  year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit', hour12: false,
});

const DISPLAY = new Intl.DateTimeFormat('en-US', {
  timeZone: EASTERN_TZ,
  month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
});

/**
 * An instant as an `<input type="datetime-local">` value in Eastern time,
 * e.g. "2026-10-07T21:30". Floored to the minute, as the input is.
 */
export function toEasternInputValue(instant: Date | string = new Date()): string {
  const d = instant instanceof Date ? instant : new Date(instant);
  if (Number.isNaN(d.getTime())) return '';
  const parts = PARTS.formatToParts(d);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  // hour12:false prints midnight as "24" in some engines.
  const hour = get('hour') === '24' ? '00' : get('hour');
  return `${get('year')}-${get('month')}-${get('day')}T${hour}:${get('minute')}`;
}

/** "Oct 7, 2026, 9:30 PM ET", or "—" when unset. */
export function fmtSentAt(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : `${DISPLAY.format(d)} ET`;
}

/**
 * Run a sent-time save, then refresh — also when it is refused, keeping the
 * refusal for the dialog to show. A 409 means the RFI changed under the
 * dialog (another tab marked it sent, or cleared it), and without the refetch
 * the page would keep offering an action that no longer applies.
 */
export function saveThenRefresh(save: Promise<unknown>, refresh: () => unknown): Promise<unknown> {
  // A refresh that fails must never turn a save that succeeded into an error:
  // the dialog would stay open, and a retry would hit a 409 for a send that is
  // already recorded. So its failure is swallowed on both paths.
  const quietly = () => Promise.resolve().then(refresh).catch(() => undefined);
  return save.then(quietly, (e) => {
    void quietly();
    throw e;
  });
}
